import { compact, has, isEmpty, map, uniq } from 'lodash';

/**
 * POM-6279 P5 — what New Order says once createPomOrder answers.
 *
 * ROUTING_RULES can send an order, or some of its lines, to a partner pharmacy (RELAYED) or hold
 * it for review (PARKED) instead of creating it in POM, and "successfully created" is wrong for
 * both. Core reports that as `routingOutcome` (null on REJECTED) plus per-line `lineRouting`.
 *
 * The Non-Routable Orders queue is SYS_ADMIN only, while createPomOrder is open to pharmacists and
 * techs, so ONLY an admin is pointed at that queue. Anything unknown renders as its raw value.
 *
 * A pure module so it can be tested on its own: CreateOrderForm imports common/utils, whose ESM
 * dependencies do not load under this repo's Jest setup.
 */

export const ROUTING_OUTCOME = {
  CREATED: 'CREATED',
  RELAYED: 'RELAYED',
  PARTIALLY_RELAYED: 'PARTIALLY_RELAYED',
  PARKED: 'PARKED',
  PARTIALLY_PARKED: 'PARTIALLY_PARKED',
};

const RESPONSE_TYPE = {
  ACCEPTED: 'ACCEPTED',
  ACCEPTED_WITH_ERRORS: 'ACCEPTED_WITH_ERRORS',
};

const CREATED_MESSAGE = {
  [RESPONSE_TYPE.ACCEPTED]: 'The order has been successfully created.',
  [RESPONSE_TYPE.ACCEPTED_WITH_ERRORS]:
    'The order has been successfully created with some warnings',
};

const REJECTED_MESSAGE = 'The Order was rejected as the prescription has some invalid data';

const ROUTED_MESSAGE = {
  [ROUTING_OUTCOME.RELAYED]: 'The order has been sent to the partner pharmacy.',
  [ROUTING_OUTCOME.PARTIALLY_RELAYED]:
    'The order has been created. Some prescriptions were sent to the partner pharmacy.',
  [ROUTING_OUTCOME.PARKED]: 'The order has been held for review.',
  [ROUTING_OUTCOME.PARTIALLY_PARKED]:
    'The order has been created. Some prescriptions were held for review.',
};

const PARKED_OUTCOMES = [ROUTING_OUTCOME.PARKED, ROUTING_OUTCOME.PARTIALLY_PARKED];

const LINE_OUTCOME = { POM: 'POM', RELAYED: 'RELAYED', PARKED: 'PARKED' };

const ROUTED_OUTCOMES = [
  ROUTING_OUTCOME.RELAYED,
  ROUTING_OUTCOME.PARTIALLY_RELAYED,
  ROUTING_OUTCOME.PARKED,
  ROUTING_OUTCOME.PARTIALLY_PARKED,
];

/**
 * The outcome the WORDING follows, derived from the lines where they say more than the summary.
 *
 * Core's lines come from one execution (pomOrderRuleRouting/routingOutcome.ts): all POM, POM +
 * RELAYED, all PARKED, or POM + PARKED. Its routingOutcome comes from the plan, and an impossible
 * plan/execution pair falls back by execution -- RELAY_ONLY + PARKED_RELAY_LINES answers
 * PARTIALLY_PARKED with EVERY line parked, where "the order has been created" is false. So:
 *
 * - a POM line exists -> partial ("created", plus what left): PARTIALLY_PARKED if any line is held,
 *   else PARTIALLY_RELAYED;
 * - no POM line -> whole: PARKED if any line is held (the conservative reading of a mix no single
 *   execution produces), else RELAYED;
 * - every line POM, or no lines -> the summary as sent. That keeps core's own "relayed without a
 *   plan" (PARTIALLY_RELAYED, all lines POM, unknown which went) honest.
 *
 * Applied only to the four routed outcomes: CREATED/null stays created, and an outcome this portal
 * does not know keeps its raw-value wording.
 */
export const effectiveRoutingOutcome = ({ routingOutcome, lineRouting }) => {
  if (!ROUTED_OUTCOMES.includes(routingOutcome) || isEmpty(lineRouting)) return routingOutcome;

  const outcomes = uniq(map(lineRouting, 'outcome'));
  const hasPom = outcomes.includes(LINE_OUTCOME.POM);
  const hasParked = outcomes.includes(LINE_OUTCOME.PARKED);

  if (hasPom && outcomes.length === 1) return routingOutcome;

  if (hasPom) {
    return hasParked ? ROUTING_OUTCOME.PARTIALLY_PARKED : ROUTING_OUTCOME.PARTIALLY_RELAYED;
  }

  return hasParked ? ROUTING_OUTCOME.PARKED : ROUTING_OUTCOME.RELAYED;
};

const NON_ROUTABLE_QUEUE_HINT = 'See the Non-Routable Orders queue.';
const WARNINGS_NOTE = 'Some prescriptions have warnings.';

const isAccepted = (responseType) => Boolean(CREATED_MESSAGE[responseType]);

const isCreatedInPom = (routingOutcome) =>
  !routingOutcome || routingOutcome === ROUTING_OUTCOME.CREATED;

export const orderSubmissionMessage = ({
  responseType,
  routingOutcome: reportedOutcome,
  lineRouting,
  canViewNonRoutableOrders,
}) => {
  if (!isAccepted(responseType)) return REJECTED_MESSAGE;

  const routingOutcome = effectiveRoutingOutcome({ routingOutcome: reportedOutcome, lineRouting });

  if (isCreatedInPom(routingOutcome)) return CREATED_MESSAGE[responseType];

  const routed =
    ROUTED_MESSAGE[routingOutcome] ??
    `The order was submitted (routing outcome: ${routingOutcome}).`;

  return compact([
    routed,
    canViewNonRoutableOrders && PARKED_OUTCOMES.includes(routingOutcome) && NON_ROUTABLE_QUEUE_HINT,
    responseType === RESPONSE_TYPE.ACCEPTED_WITH_ERRORS && WARNINGS_NOTE,
  ]).join(' ');
};

/** success | warning | error — drives the result icon. Held or unknown is never a green tick. */
export const orderSubmissionTone = ({
  responseType,
  routingOutcome: reportedOutcome,
  lineRouting,
}) => {
  if (!isAccepted(responseType)) return 'error';

  const routingOutcome = effectiveRoutingOutcome({ routingOutcome: reportedOutcome, lineRouting });

  if (responseType === RESPONSE_TYPE.ACCEPTED_WITH_ERRORS) return 'warning';
  if (isCreatedInPom(routingOutcome)) return 'success';

  return [ROUTING_OUTCOME.RELAYED, ROUTING_OUTCOME.PARTIALLY_RELAYED].includes(routingOutcome)
    ? 'success'
    : 'warning';
};

const LINE_ROUTING_LABEL = {
  POM: null,
  RELAYED: 'Sent to the partner pharmacy',
  PARKED: 'Held for review',
};

/** The note for one line, or null for a line that stayed in POM. Unknown -> the raw value. */
export const lineRoutingLabel = (outcome) =>
  has(LINE_ROUTING_LABEL, outcome) ? LINE_ROUTING_LABEL[outcome] : String(outcome);

/**
 * When the lines went different ways, the ones that did NOT stay in POM, named by drug and
 * strength. Lines that all went one way are already said by the message, so they get no per-line
 * repetition. `lineIndex` is positional to the submitted prescriptions (OTC items are not listed);
 * an index with no prescription is still reported, by position, rather than dropped.
 */
export const routedLineNotes = ({ lineRouting, prescriptions }) =>
  (uniq(map(lineRouting, 'outcome')).length > 1 ? lineRouting : [])
    .filter(({ outcome }) => lineRoutingLabel(outcome) !== null)
    .map(({ lineIndex, outcome }) => {
      const prescription = prescriptions?.[lineIndex];

      return {
        key: `${lineIndex}-${outcome}`,
        drug: prescription
          ? compact([prescription.drugName, prescription.strength]).join(' ')
          : `Prescription ${lineIndex + 1}`,
        label: lineRoutingLabel(outcome),
      };
    });
